import { useAchievementTypes, usePreviousPage, useReduxState } from 'hooks';
import { Owners } from 'components/common';
import { ErrorPage } from 'components/error';
import {
  Page,
  PageContent,
  PageHeader,
  PageInnerContent,
  PageSidebar,
  PageSidebarTitle
} from 'components/page';

interface Props {
  children: React.ReactNode;
  ensureAdminPermission?: string;
  ensureOwner?: boolean;
  ensureOwnerOrAdmin?: boolean;
  navActiveLink: string;
}

const AchievementPage = ({
  children,
  ensureAdminPermission,
  ensureOwner = true,
  ensureOwnerOrAdmin = false,
  navActiveLink
}: Props) => {
  const {
    messages,
    pageState: { achievement, achievementDataFetched },
    user
  } = useReduxState();

  const { as, href, title } = usePreviousPage({ fallbackUrl: '/achievements' });

  const {
    hasTemplate,
    icon,
    isGroupAchievement,
    title: achievementTitle
  } = useAchievementTypes({
    type: achievement?.type
  });

  if (!achievementDataFetched) {
    // this happens when navigating between pages
    return null;
  } else if (
    !achievement ||
    (ensureOwnerOrAdmin &&
      !(
        achievement.isOwnerOrSuperadmin ||
        achievement.userAdminPermissions?.length
      ))
  ) {
    return (
      <ErrorPage
        description={messages.errorPermissionMaybe}
        icon="check-circle"
        title={messages.achievementNotFound}
      />
    );
  } else if (
    (ensureOwner && !achievement.isOwnerOrSuperadmin) ||
    (ensureAdminPermission &&
      !achievement.isOwnerOrSuperadmin &&
      !achievement.userAdminPermissions?.includes(ensureAdminPermission))
  ) {
    return (
      <ErrorPage
        description={messages.errorPermission}
        icon="check-circle"
        title={messages.errorForbidden}
      />
    );
  }

  const userIsSuperadmin = user?.roles.includes('superadmin');

  const hasFullAccess =
    userIsSuperadmin ||
    achievement?.owners.findIndex((owner) => owner.id === user?.id) !== -1;

  const links = [
    ...(hasTemplate && hasFullAccess
      ? [
          {
            href: `/admin/achievements/${achievement.id}`,
            icon:
              achievement.type === 'tasklist'
                ? ('clipboard-list' as const)
                : ('comments' as const),
            title:
              achievement.type === 'tasklist'
                ? messages.tasks
                : messages.commonQuestions
          }
        ]
      : []),
    ...(hasFullAccess || achievement.userAdminPermissions?.length
      ? isGroupAchievement
        ? [
            {
              href: `/admin/achievements/${achievement.id}/group-conversations`,
              icon: 'user-friends' as const,
              title: messages.achievementGroupConversations
            }
          ]
        : [
            {
              href: `/admin/achievements/${achievement.id}/users`,
              icon: 'user' as const,
              title: messages.commonUsers,
              unreadItems: achievement.numberOfPendingAccessRequests
            }
          ]
      : []),
    ...(userIsSuperadmin
      ? [
          {
            href: `/admin/achievements/${achievement.id}/supervisors`,
            icon: 'sitemap' as const,
            title: messages.adminSupervisors
          }
        ]
      : achievement.isSupervisor
      ? [
          {
            href: `/admin/achievements/${achievement.id}/my-team`,
            icon: 'sitemap' as const,
            title: messages.myTeamPage,
            unreadItems: achievement.numberOfPendingAccessRequestsForSupervisors
          }
        ]
      : []),
    ...(hasFullAccess
      ? [
          {
            href: `/admin/achievements/${achievement.id}/settings`,
            icon: 'cog' as const,
            title: messages.commonSettings
          },
          {
            href: `/admin/achievements/${achievement.id}/completion-settings`,
            icon: 'check-circle' as const,
            title: messages.achievementCompletionDetails
          },
          ...(achievement.type === 'external'
            ? [
                {
                  href: `/admin/achievements/${achievement.id}/competencies`,
                  icon: 'medal' as const,
                  title: messages.commonCompetencies
                }
              ]
            : []),
          {
            href: `/admin/achievements/${achievement.id}/access`,
            icon: 'eye' as const,
            title: messages.eventCalendarSettingsAccess
          },
          {
            href: `/admin/achievements/${achievement.id}/events`,
            icon: 'list' as const,
            title: messages.commonActivityLog
          },
          {
            href: `/admin/achievements/${achievement.id}/manage`,
            icon: 'tools' as const,
            title: messages.pagesManage
          }
        ]
      : [])
  ];

  return (
    <Page activePathname="/achievements">
      <PageHeader backLinkAs={as} backLinkHref={href} title={title} />

      <PageContent withSidebar={true}>
        <PageSidebar
          active={`/admin/achievements/${achievement.id}${navActiveLink || ''}`}
          isWide={true}
          links={links}
        >
          <PageSidebarTitle
            icon={icon}
            subtitle={
              <div>
                <div>{achievementTitle}</div>
                <div>
                  <Owners owners={achievement.owners} />
                </div>
              </div>
            }
            title={achievement.title}
          />
        </PageSidebar>
        <PageInnerContent>{children}</PageInnerContent>
      </PageContent>
    </Page>
  );
};

export default AchievementPage;
